import React from 'react';
import {
  Brain, Search, FileText, PenTool, MapPin, Zap, BarChart3, Globe,
  Sparkles, MessageSquare, Image, FileCheck, Star
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FeaturesSection = () => {
  const { t } = useTranslation();
  const agents = [
    { id: 'orchestratore', icon: Brain, color: '#0055FF', span: 'col-span-12 md:col-span-8',
      image: 'https://images.pexels.com/photos/1103970/pexels-photo-1103970.jpeg?w=800' },
    { id: 'aeo-analysis', icon: Sparkles, color: '#8B5CF6', span: 'col-span-12 md:col-span-4' },
    { id: 'aeo-content',  icon: MessageSquare, color: '#EC4899', span: 'col-span-12 md:col-span-4' },
    { id: 'analisi',      icon: Search, color: '#00E5FF', span: 'col-span-12 md:col-span-4' },
    { id: 'contenuto',    icon: PenTool, color: '#F59E0B', span: 'col-span-12 md:col-span-4', badge: t('features.badge.premium') },
    { id: 'metatags',     icon: FileText, color: '#10B981', span: 'col-span-12 md:col-span-6' },
    { id: 'localseo',     icon: MapPin, color: '#EF4444', span: 'col-span-12 md:col-span-6' },
  ];

  const additionalFeatures = [
    { icon: Image,     id: 'aiImages' },
    { icon: FileCheck, id: 'preview' },
    { icon: Zap,       id: 'bulk' },
    { icon: BarChart3, id: 'history' },
    { icon: Globe,     id: 'multilang' },
    { icon: Star,      id: 'widget' },
  ];

  return (
    <section id="features" className="py-24 px-6" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-up">
          <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-semibold">{t('features.eyebrow')}</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mt-4 mb-6">
            {t('features.title')}
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="bento-grid">
          {agents.map((agent, index) => (
            <div 
              key={agent.id}
              className={`card-bento ${agent.span} animate-fade-up relative`}
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`feature-card-${agent.id}`}
            >
              {agent.image && (
                <div className="absolute inset-0 opacity-20">
                  <img src={agent.image} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent"></div>
                </div>
              )}
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${agent.color}20` }}
                  >
                    <agent.icon className="w-7 h-7" style={{ color: agent.color }} strokeWidth={1.5} />
                  </div>
                  {agent.badge && (
                    <span className="px-3 py-1 rounded-full text-xs bg-[#7c3aed]/15 text-[#c4b5fd] border border-[#7c3aed]/30">
                      {agent.badge}
                    </span>
                  )}
                </div>
                
                <h3 className="font-['Outfit'] text-2xl font-medium mb-3">{t('features.agents.' + agent.id + '.name')}</h3>
                <p className="text-[#A1A1AA] leading-relaxed">{t('features.agents.' + agent.id + '.desc')}</p>

                {agent.id === 'orchestratore' && (
                  <div className="mt-6 flex items-center gap-4">
                    <div className="flex -space-x-2">
                      {['#8B5CF6', '#EC4899', '#00E5FF', '#10B981', '#F59E0B', '#EF4444'].map((color, i) => (
                        <div key={i} className="w-8 h-8 rounded-full border-2 border-[#111111]" style={{ backgroundColor: color }}></div>
                      ))}
                    </div>
                    <span className="text-sm text-[#A1A1AA]">{t('features.coordinates6')}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Features */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {additionalFeatures.map((feature, index) => (
            <div
              key={feature.id}
              className="flex items-start gap-4 p-6 rounded-2xl bg-[#111111]/50 border border-white/5 animate-fade-up"
              style={{ animationDelay: `${0.5 + index * 0.1}s` }}
            >
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-5 h-5 text-[#0055FF]" strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="font-['Outfit'] font-medium mb-1">{t('features.extra.' + feature.id + '.title')}</h4>
                <p className="text-sm text-[#A1A1AA]">{t('features.extra.' + feature.id + '.desc')}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
